/* eslint-disable @typescript-eslint/no-explicit-any */
// Libraries
import React, { useMemo, useCallback, useRef } from 'react'
import { Alert, AlertTitle, Box, Collapse } from '@mui/material'
import { useDispatch } from 'react-redux'

// Components
import CustomButton from '../../CustomButton/CustomButton'
import { ModalProvider, useModal } from '../../../../../store/ModalProvider'

// Types and Interfaces
import { MassApprovalAPIResponse } from '../../../../../types/massApproval'
import { MassApprovalRequest } from '../../../../../store/slices/timesheet/timesheetApprovalDetails/timesheetApprovalDetailsSlice'

// Utils
import { useSignalR } from '../../../../../utils/hooks/signalR'
import { useGetTimesheetsFilteredQuery } from '../../../../../utils/hooks/useGetTimesheetsFilteredQuery'
import { timesheetApi } from '../../../../../store/api/timesheet/timesheetApi'
import {
	resetProgress,
	setData,
	useApproveTimesheetBatchMutation,
} from '../../../../../store/slices/timesheet/massApprovalSlice'
import { devWarn, devLog, devError } from '../../../../../utils/feedback'

/**
 * Props for the CustomSnackbarMassApprovalError component.
 */
interface CustomSnackbarMassApprovalError {
	closeHandler: () => void
	data: MassApprovalAPIResponse
	isOpen: boolean
}

/**
 * ## Shows an error snackbar indicating that timesheets without changes failed to be approved.
 * It is controlled by the parent component via the isOpen and the closeHandler props.
 * - @param isOpen - Controls whether the snackbar is open
 * - @param closeHandler - Function to handle closing the snackbar
 * - @returns JSX.Element representing the CustomSnackbarMassApprovalError component
 */
const CustomSnackbarMassApprovalError: React.FC<CustomSnackbarMassApprovalError> = ({ closeHandler, data, isOpen }) => {
	// Ref to track event listener registration
	const eventListenerRegisteredRef = useRef(false)

	const { openModal, closeModal } = useModal()

	const { data: timesheets } = useGetTimesheetsFilteredQuery()

	const [approveTimesheetBatch] = useApproveTimesheetBatchMutation()

	const dispatch = useDispatch()

	/**
	 * Handles opening the details modal when the "Ver Detalhes" button is clicked.
	 * Closes the snackbar and opens a modal with error details and a retry option.
	 * The modal allows users to review the timesheets that were submitted without changes and retry the approval if needed.
	 * This function is memoized with useCallback to prevent unnecessary re-renders and ensure stable references for event handlers.
	 * It depends on the closeModal function from the ModalProvider, the dispatch function from Redux, and the approveTimesheetBatch mutation for retrying failed approvals.
	 * The data passed to the modal includes the details of the timesheets that were submitted without changes, allowing users to understand the context of the error and take appropriate action.
	 * The retry handler is passed to the modal to allow users to attempt the approval process again after reviewing the details, providing a seamless user experience for error recovery.
	 * Error handling is implemented to catch any issues that may arise during the retry process, including specific handling for browser extension communication errors and SignalR connection issues, ensuring that the user is informed of any problems without disrupting the overall functionality of the application.
	 * The function also ensures that the SignalR connection is properly managed, starting it when needed for real-time updates and stopping it after the operation is complete, with appropriate logging for debugging purposes.
	 * Overall, this function enhances the user experience by providing clear feedback on errors, allowing users to review details, and offering a straightforward path to retry the operation, all while maintaining robust error handling and state management.
	 * @param closeModal - Function to close the snackbar and open the modal
	 * @param data - The data containing details of the timesheets that were submitted without changes, passed to the modal for display
	 * @param handleRetryFailedApprovals - The function to handle retrying the approval of timesheets, passed to the modal for user interaction
	 * @return A function that handles opening the details modal with error information and retry option
	 * @throws Will log errors to the console if the retry process encounters issues, but will not throw errors to the caller to prevent unhandled promise rejections
	 * @see handleRetryFailedApprovals for the function that handles the retry logic when the user clicks the retry button in the modal
	 * @see useSignalR for managing the SignalR connection used for real-time updates during the retry process
	 * @see useApproveTimesheetBatchMutation for the mutation used to trigger the approval of timesheets during the retry process
	 * @see resetProgress and setData for managing the progress state in the Redux store during the retry process
	 * @see timesheetApi.util.invalidateTags for revalidating the timesheet totals after the retry process is complete
	 * @see devLog, devWarn, and devError for logging important information and errors during the retry process to aid in debugging and provide insights into the application's behavior during error handling and recovery
	 */
	const signalROptions = useMemo(
		() => ({
			autoStart: false,
			withCredentials: false,
			retryAttempts: 2,
			fallbackUrls: [
				`${process.env.REACT_APP_API_BASE_URL}/hubs/progress`,
				`${process.env.REACT_APP_API_BASE_URL}/api/progress-hub`,
			],
		}),
		[],
	)

	/**
	 * Handles retrying the approval of timesheets that were submitted without changes.
	 * This function is called when the user clicks the retry button in the error details modal.
	 * It attempts to re-approve the timesheets, manages the SignalR connection for real-time updates, and handles any errors that may occur during the process.
	 * The function is memoized with useCallback to ensure that it maintains a stable reference and does not cause unnecessary re-renders of the modal or its children.
	 * It also includes robust error handling to catch and log any issues that arise during the retry process, including specific handling for browser extension communication errors and SignalR connection issues, ensuring that the user is informed of any problems without disrupting the overall functionality of the application.
	 * Finally, it ensures that the SignalR connection is properly cleaned up after the operation is complete, regardless of success or failure, to prevent any potential memory leaks or lingering connections.
	 * @returns A promise that resolves when the retry process is complete, allowing the calling component to handle any necessary state updates or UI changes after the retry attempt.
	 * @throws Will log errors to the console if the retry process encounters issues, but will not throw errors to the caller to prevent unhandled promise rejections.
	 * @see useSignalR for managing the SignalR connection used for real-time updates during the retry process
	 * @see useApproveTimesheetBatchMutation for the mutation used to trigger the approval of timesheets during the retry process
	 * @see resetProgress and setData for managing the progress state in the Redux store during the retry process
	 * @see timesheetApi.util.invalidateTags for revalidating the timesheet totals after the retry process is complete
	 * @see devLog, devWarn, and devError for logging important information and errors during the retry process to aid in debugging and provide insights into the application's behavior during error handling and recovery
	 */
	const { startConnection, stopConnection } = useSignalR(
		`${process.env.REACT_APP_API_BASE_URL?.replace('/api/v1', '')}/progressHub`,
		[],
		signalROptions,
	)

	/**
	 * Handles retrying the approval of timesheets that were submitted without changes.
	 * This function is called when the user clicks the retry button in the error details modal.
	 * It attempts to re-approve the timesheets, manages the SignalR connection for real-time updates, and handles any errors that may occur during the process.
	 * The function is memoized with useCallback to ensure that it maintains a stable reference and does not cause unnecessary re-renders of the modal or its children.
	 * It also includes robust error handling to catch and log any issues that arise during the retry process, including specific handling for browser extension communication errors and SignalR connection issues, ensuring that the user is informed of any problems without disrupting the overall functionality of the application.
	 * Finally, it ensures that the SignalR connection is properly cleaned up after the operation is complete, regardless of success or failure, to prevent any potential memory leaks or lingering connections.
	 * @returns A promise that resolves when the retry process is complete, allowing the calling component to handle any necessary state updates or UI changes after the retry attempt.
	 * @throws Will log errors to the console if the retry process encounters issues, but will not throw errors to the caller to prevent unhandled promise rejections.
	 */
	const handleRetryFailedApprovals = useCallback(async () => {
		closeModal()

		try {
			// Reset progress before starting with error handling for browser extension issues
			try {
				dispatch(resetProgress())
			} catch (dispatchError) {
				if (
					typeof dispatchError === 'object' &&
					dispatchError !== null &&
					'message' in dispatchError &&
					typeof (dispatchError as any).message === 'string' &&
					((dispatchError as any).message.includes('disconnected port') ||
						(dispatchError as any).message.includes('port object'))
				) {
					devWarn('[SubmittedWithoutChanges] - Browser extension communication error, continuing...')
				} else {
					throw dispatchError
				}
			}

			// Try to start SignalR connection (optional for progress updates)
			let signalRConnected = false
			try {
				await startConnection()
				signalRConnected = true
				devLog('[SubmittedWithoutChanges] - SignalR connection started')
			} catch (startError) {
				devWarn(
					'[SubmittedWithoutChanges] - SignalR connection failed, continuing without real-time updates:',
					startError,
				)
				// Continue without SignalR - it's not critical for the operation
			}
			devLog('[SubmittedWithoutChanges] - SignalR connection status after start attempt:', signalRConnected)
			// Trigger the approval mutation
			const massApprovalRequests: MassApprovalRequest[] = (timesheets?.itemList ?? []).map(ts => ({
				comments: ts.comments ?? '',
				driverName: ts.driverName ?? '',
				status: 'SubmittedWithoutChanges' as const,
				timeSheetId: ts.timeSheetId ?? 0,
				timesheetDate: ts.timesheetDate ?? '',
				totalServices: ts.totalServices ?? 0,
			}))
			const result = await approveTimesheetBatch(massApprovalRequests).unwrap()

			if (result) {
				dispatch(setData(result))
			}
		} catch (error) {
			devError('[SubmittedWithoutChanges] - Failed to approve timesheets:', error)

			// Handle specific error types
			if (error && typeof error === 'object' && 'status' in error) {
				const statusError = error as { status: number; data?: any }
				if (statusError.status === 500) {
					devError('[SubmittedWithoutChanges] - Server error (500) occurred')
				}
			}

			// Don't re-throw the error to prevent unhandled promise rejection
		} finally {
			// Clean up SignalR connection if it was started
			try {
				await stopConnection()
				devLog('[SubmittedWithoutChanges] - SignalR connection stopped')
			} catch (stopError) {
				devWarn('[SubmittedWithoutChanges] - Error stopping SignalR connection:', stopError)
			}
			eventListenerRegisteredRef.current = false

			// Revalidate timesheet totals tag to refresh data
			await new Promise(resolve => setTimeout(resolve, 500)) // Slight delay to ensure backend processing
			dispatch(timesheetApi.util.invalidateTags([{ type: 'TimesheetTotals' }]))
		}
	}, [closeModal, dispatch, startConnection, stopConnection, approveTimesheetBatch, timesheets?.itemList])

	/**
	 * Handles opening the details modal when the "Ver Detalhes" button is clicked.
	 * Closes the snackbar and opens a modal with error details and a retry option.
	 * The modal allows users to review the timesheets that were submitted without changes and retry the approval if needed.
	 * This function is memoized with useCallback to prevent unnecessary re-renders and ensure stable references for event handlers.
	 * It depends on the closeModal function from the ModalProvider, the dispatch function from Redux, and the approveTimesheetBatch mutation for retrying failed approvals.
	 * The data passed to the modal includes the details of the timesheets that were submitted without changes, allowing users to understand the context of the error and take appropriate action.
	 * The retry handler is passed to the modal to allow users to attempt the approval process again after reviewing the details, providing a seamless user experience for error recovery.
	 * Error handling is implemented to catch any issues that may arise during the retry process, including specific handling for browser extension communication errors and SignalR connection issues, ensuring that the user is informed of any problems without disrupting the overall functionality of the application.
	 * The function also ensures that the SignalR connection is properly managed, starting it when needed for real-time updates and stopping it after the operation is complete, with appropriate logging for debugging purposes.
	 * Overall, this function enhances the user experience by providing clear feedback on errors, allowing users to review details, and offering a straightforward path to retry the operation, all while maintaining robust error handling and state management.
	 * @param closeModal - Function to close the snackbar and open the modal
	 * @param data - The data containing details of the timesheets that were submitted without changes, passed to the modal for display
	 * @param handleRetryFailedApprovals - The function to handle retrying the approval of timesheets, passed to the modal for user interaction
	 */
	const handleOpenDetailsModal = () => {
		closeHandler() // Closes the snackbar has it oppens the modal
		openModal('Rever Folhas de Ponto Submetidas sem Alterações', data, [handleRetryFailedApprovals])
	}

	if (!isOpen) return null

	return (
		<ModalProvider>
			<Box sx={{ width: '100%', position: 'absolute', top: '-24px', left: 0, zIndex: 999 }}>
				<Collapse
					in={isOpen}
					timeout={150}
					easing={'ease'}
					exit={true}
				>
					<Alert
						variant='filled'
						severity='error'
						title='Erro na aprovação'
						aria-description='Snackbar de aprovação em massa com erro'
						onClose={closeHandler}
						sx={{
							height: '37px',
							padding: '0 16px',
							display: 'flex',
							alignItems: 'center',
							background: 'linear-gradient(90deg, #D32F2F 0%, #FB736D 100%)',
							fontFamily: 'var(--font-family)',
							fontSize: '14px',
							fontWeight: 500,
							letterSpacing: '0.15px',
							lineHeight: '150%',
							'& .MuiAlert-message': {
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								gap: '1rem',
								flex: 1,
							},
							'& .MuiAlert-action': {
								width: 'fit-content !important',
							},
						}}
					>
						<AlertTitle
							sx={{
								width: 'fit-content',
								margin: 0,
								color: '#FFFFFF',
								fontFamily: 'Poppins, sans-serif',
								fontSize: '14px',
								fontStyle: 'normal',
								fontWeight: 500,
								letterSpacing: '0.15px',
								lineHeight: '150%',
							}}
						>
							Erro na aprovação
						</AlertTitle>
						<CustomButton
							size='small'
							color='error'
							onClick={handleOpenDetailsModal}
							sx={{
								width: 'fit-content',
								marginLeft: 'auto',
								backgroundColor: 'transparent',
								boxShadow: 'none',
							}}
						>
							Ver Detalhes
						</CustomButton>
					</Alert>
				</Collapse>
			</Box>
		</ModalProvider>
	)
}

export default CustomSnackbarMassApprovalError
